import React, { useContext } from "react";
import { Home, User, LogOut, MessageCircle } from "react-feather";
import { FaComments } from 'react-icons/fa';
import { useNavigate, useLocation } from "react-router-dom";
import Login from "../pages/Login";
import AppContext from "../api/AppContext";
import api from "../services/api";

const NavItem = ({ icon, text, active = false, isLogout = false, onClick }) => {
  const [hover, setHover] = React.useState(false);
  const styles = getNavItemStyles(active, isLogout, hover);

  return (
    <div
      style={styles.item}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick || (() => {})}
      role="button"
      tabIndex={0}
    >
      <span style={styles.icon}>{icon}</span>
      <span>{text}</span>
    </div>
  );
};

const MobileNavItem = ({ icon, text, active = false, isLogout = false, onClick }) => {
  const styles = getMobileNavItemStyles(active, isLogout);

  return (
    <div
      style={styles.item}
      onClick={onClick || (() => {})}
      role="button"
      tabIndex={0}
    >
      <span>{icon}</span>
      <span style={styles.text}>{text}</span>
    </div>
  );
};

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDesktop, setIsDesktop] = React.useState(false);
  const styles = getNavbarStyles(isDesktop);
  const { isAuthenticated, user, logout, socket } = useContext(AppContext);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);

  React.useEffect(() => {
    const fetchUnreadCount = async () => {
      if (isAuthenticated) {
        try {
          const res = await api.get('/chat/unread-count');
          const data = res.data;
          if (data?.data?.unreadCount !== undefined) {
            setUnreadCount(data.data.unreadCount);
          }
        } catch (err) {
          console.error('Erro ao buscar mensagens não lidas:', err);
        }
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    
    const handleStorageChange = (e) => {
      if (e.key === 'chat-unread-update') {
        fetchUnreadCount();
      }
    };
    const handleUnreadUpdateEvent = () => {
      fetchUnreadCount();
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('chat-unread-update', handleUnreadUpdateEvent);
    
    const handleNewMessage = (data) => {
      // Só conta mensagens de membro (resposta da secretaria/transmissão não gera badge)
      if (data?.message?.sender?.role && data.message.sender.role !== 'member') return;
      // Ignora a própria mensagem (eco do socket)
      if (data?.message?.sender?.userId === user?._id) return;
      setUnreadCount(prev => prev + 1);
    };
    
    if (socket) {
      socket.on('chat:message', handleNewMessage);
      socket.on('chat:new-message', handleNewMessage);
      socket.on('chat:notification', handleNewMessage);
    }
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('chat-unread-update', handleUnreadUpdateEvent);
if (socket) {
      socket.off('chat:message', handleNewMessage);
      socket.off('chat:new-message', handleNewMessage);
      socket.off('chat:notification', handleNewMessage);
    }
    };
  }, [user, isAuthenticated, socket]);

  React.useEffect(() => {
    setIsDesktop(window.innerWidth >= 768);
    const check = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleHomeClick = () => navigate("/");

  const handleProfileClick = () => {
    const perfilPath = user?.role === 'member' ? '/member/perfil' : '/admin/perfil';
    if (!isAuthenticated || !user) {
      window.location.href = '/login';
    } else {
      navigate(perfilPath)
      //window.location.href = perfilPath;
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isActiveHome = location.pathname === "/";

  if (!isAuthenticated) return null;

  return (
    <>
      {/* Navbar Desktop */}
      <nav style={styles.desktopNavbar}>
        <div>
          <img style={styles.logo} src="/logo.png" alt="Logo" />
          <NavItem
            icon={<Home size={20} />}
            text="Início"
            active={isActiveHome}
            onClick={handleHomeClick}
          />
          <NavItem 
            icon={<User size={20} />} 
            text="Perfil" 
            onClick={handleProfileClick}
          />
          {/* Chat visível apenas para membros */}
          {user?.role === 'member' && (
            <div style={{ position: 'relative' }}>
              <NavItem 
                icon={<MessageCircle size={20} />} 
                text="Chat" 
                onClick={() => navigate("/member/chat")}
              />
              {unreadCount > 0 && (
                <span style={{ position: 'absolute', top: '8px', right: '20px', background: '#e74c3c', color: 'white', borderRadius: '50%', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </div>
          )}
        </div>
        <div style={styles.logoutContainer}>
          <NavItem 
            icon={<LogOut size={20} />} 
            text="Sair" 
            isLogout={true} 
            onClick={handleLogout}
          />
        </div>
      </nav>

      {/* Navbar Mobile */}
      <nav style={styles.mobileNavbar}>
        <MobileNavItem
          icon={<Home size={24} />}
          text="Início"
          active={isActiveHome}
          onClick={handleHomeClick}
        />
        <MobileNavItem 
          icon={<User size={24} />} 
          text="Perfil" 
          onClick={handleProfileClick}
        />
        {/* Chat visível apenas para membros */}
        {user?.role === 'member' && (
          <div style={{ position: 'relative' }}>
            <MobileNavItem 
              icon={<MessageCircle size={24} />} 
              text="Chat" 
              onClick={() => navigate("/member/chat")}
            />
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: '2px', right: '10px', background: '#e74c3c', color: 'white', borderRadius: '50%', padding: '2px 5px', fontSize: '9px', fontWeight: 'bold' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </div>
        )}
        {/* Item de logout comentado, conforme original */}
      </nav>
    </>
  );
};

// === Estilos da Navbar ===
const getNavbarStyles = (isDesktop) => ({
  desktopNavbar: {
    display: isDesktop ? "flex" : "none",
    flexDirection: "column",
    justifyContent: "space-between",
    backgroundColor: "#3871c1",
    color: "white",
    height: "100vh",
    width: "16rem",
    position: "fixed",
    left: 0,
    top: 0,
    padding: "1rem",
  },
  mobileNavbar: {
    display: isDesktop ? "none" : "flex",
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    height: "60px",
    backgroundColor: "#2563eb",
    color: "white",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "0.5rem",
    zIndex: 1001,
  },
  logo: {
    width: "100%",
    height: "116px",
    marginRight: "16px",
    objectFit: "contain",
    padding: "5px",
    display: "flex",
    paddingBottom: "2rem",
  },
  logoutContainer: {
    marginBottom: "1rem",
  },
});

// === Estilos dos Itens da Navbar ===
const getNavItemStyles = (active, isLogout, hover) => ({
  item: {
    display: "flex",
    alignItems: "center",
    padding: "0.75rem",
    borderRadius: "0.5rem",
    marginBottom: "0.5rem",
    cursor: "pointer",
    backgroundColor: active ? "#1d4ed8" : "transparent",
    color: isLogout ? "#fecaca" : "white",
    ...(hover && !active && {
      backgroundColor: isLogout ? "#ef4444" : "#3b82f6",
      ...(isLogout && { color: "white" }),
    }),
    transition: "background-color 0.2s ease, color 0.2s ease",
  },
  icon: {
    marginRight: "0.75rem",
  },
});

const getMobileNavItemStyles = (active, isLogout = false) => ({
  item: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0.5rem",
    borderRadius: "0.5rem",
    cursor: "pointer",
    backgroundColor: active ? "#1d4ed8" : "transparent",
    color: isLogout ? "#fecaca" : "white",
    transition: "background-color 0.2s ease, color 0.2s ease",
  },
  text: {
    fontSize: "0.75rem",
  },
});

export default Navbar;